import React, { useMemo, useState } from 'react';
import { ChevronDown, ChevronRight, Users2 } from 'lucide-react';
import {
  LST_CARGO_LABELS,
  type LstAderenciaNivel,
  type LstCargo,
  type LstLideradoAderencia,
} from '../types';
import { WEEKDAY_LABELS, todayISO } from '../services/lstService';
import { computeCadeiaChainInsights } from '../services/cadeiaChainInsights';
import { resolveAndonLevelForCargo } from '../services/andonEscalation';
import { GRAVIDADE_STYLE, resolveDayGravidade } from '../services/cadeiaSeverityStyles';
import { CadeiaEscalationTimeline } from './cadeia/CadeiaEscalationTimeline';
import { CadeiaRecommendedActionCard } from './cadeia/CadeiaRecommendedActionCard';
import { CadeiaExecutiveImpactCard } from './cadeia/CadeiaExecutiveImpactCard';
import { TplEnterpriseEmptyState } from './tpl/ux/TplEnterpriseEmptyState';

interface CadeiaAjudaPanelProps {
  rows: LstLideradoAderencia[];
  weekDates: string[];
  loading: boolean;
  viewerCargo: LstCargo;
  onOpenDiagnostic?: (row: LstLideradoAderencia, date: string) => void;
  activeDiagnostic?: { memberId: string; date: string } | null;
}

const NIVEL_CELL: Record<LstAderenciaNivel, string> = {
  verde: 'bg-emerald-500 hover:bg-emerald-600',
  laranja: 'bg-amber-400 hover:bg-amber-500',
  vermelho: 'bg-red-500 hover:bg-red-600',
  neutro: 'bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700',
};

function ddmm(iso: string): string {
  const [, m, d] = iso.split('-');
  return `${d}/${m}`;
}

export const CadeiaAjudaPanel: React.FC<CadeiaAjudaPanelProps> = ({
  rows,
  weekDates,
  loading,
  viewerCargo,
  onOpenDiagnostic,
  activeDiagnostic,
}) => {
  const [expanded, setExpanded] = useState<string | null>(null);
  const [focusDate, setFocusDate] = useState<string | null>(null);
  const today = todayISO();
  const viewerLevel = resolveAndonLevelForCargo(viewerCargo);

  const insights = useMemo(
    () => computeCadeiaChainInsights(rows, viewerCargo),
    [rows, viewerCargo],
  );

  const criticalById = useMemo(
    () => new Map(insights.criticalLeaders.map((c) => [c.row.member.id, c])),
    [insights.criticalLeaders],
  );

  const sortedRows = useMemo(() => {
    const order = new Map(insights.criticalLeaders.map((c, i) => [c.row.member.id, i]));
    return [...rows].sort(
      (a, b) => (order.get(a.member.id) ?? 999) - (order.get(b.member.id) ?? 999),
    );
  }, [rows, insights.criticalLeaders]);

  const openRecommendedDiagnostic = () => {
    const { recommended } = insights;
    if (!recommended.leaderId || !onOpenDiagnostic) return;
    const row = rows.find((r) => r.member.id === recommended.leaderId);
    if (!row) return;
    const date = recommended.date ?? weekDates.find((d) => d <= today) ?? weekDates[0];
    onOpenDiagnostic(row, date);
  };

  const toggle = (memberId: string, date?: string) => {
    if (expanded === memberId && (!date || focusDate === date)) {
      setExpanded(null);
      setFocusDate(null);
    } else {
      setExpanded(memberId);
      setFocusDate(date ?? null);
    }
  };

  const chipLeaders = insights.criticalLeaders.filter((c) => c.chipReason !== null);

  return (
    <div className="flex h-full min-h-0 flex-col gap-1.5 pb-4">
      <div className="flex shrink-0 flex-wrap items-end justify-between gap-2 px-0.5">
        <div>
          <h2 className="text-[14px] font-bold text-slate-800 dark:text-slate-100">Cadeia de Ajuda</h2>
          <p className="text-[10px] text-slate-500">
            {ddmm(weekDates[0])} – {ddmm(weekDates[6])}
          </p>
        </div>
        <Legend />
      </div>

      {!loading && rows.length > 0 && (
        <>
          {insights.impact && <CadeiaExecutiveImpactCard impact={insights.impact} />}

          <CadeiaRecommendedActionCard
            action={insights.recommended}
            onOpenDiagnostic={
              insights.recommended.gravidade !== 'normal' ? openRecommendedDiagnostic : undefined
            }
          />

          <CadeiaEscalationTimeline
            viewerCargo={viewerCargo}
            viewerLevel={viewerLevel}
            levels={insights.andonLevels}
            chainHasIssue={insights.chainHasIssue}
          />

          {chipLeaders.length > 0 && (
            <div className="flex shrink-0 flex-wrap gap-1.5">
              {chipLeaders.slice(0, 4).map((c) => {
                const gStyle = GRAVIDADE_STYLE[c.gravidade];
                return (
                  <button
                    key={c.row.member.id}
                    type="button"
                    onClick={() => {
                      const date =
                        c.row.dias.find((d) => d.nivel === 'vermelho')?.date ??
                        c.row.dias.find((d) => d.nivel === 'laranja')?.date ??
                        weekDates[0];
                      onOpenDiagnostic?.(c.row, date);
                    }}
                    className={`inline-flex flex-col items-start rounded-md border px-2 py-1 text-left transition hover:opacity-90 ${gStyle.chip}`}
                  >
                    <span className="text-[10px] font-bold leading-tight">{c.row.member.name}</span>
                    <span className="text-[9px] font-normal opacity-90">{c.chipReason}</span>
                  </button>
                );
              })}
            </div>
          )}
        </>
      )}

      <div className="min-h-0 flex-1 overflow-auto rounded-lg border border-slate-200 dark:border-slate-800">
        <div className="sticky top-0 z-10 grid w-full grid-cols-[minmax(140px,1.1fr)_repeat(7,minmax(40px,1fr))] border-b border-slate-200 bg-slate-50 text-[9px] font-bold uppercase tracking-wide text-slate-500 dark:border-slate-800 dark:bg-slate-900">
          <div className="px-2 py-1.5">Liderado</div>
          {weekDates.map((date, i) => (
            <div
              key={date}
              className={`flex flex-col items-center justify-center py-1 ${
                date === today ? 'text-blue-600 dark:text-blue-400' : ''
              }`}
            >
              <span>{WEEKDAY_LABELS[i]}</span>
              <span className="text-[8px] font-medium text-slate-400">{ddmm(date)}</span>
            </div>
          ))}
        </div>

        {loading ? (
          <div className="py-12 text-center text-[11px] text-slate-400">Carregando aderência…</div>
        ) : rows.length === 0 ? (
          <EmptyState />
        ) : (
          sortedRows.map((row) => {
            const score = criticalById.get(row.member.id);
            const gravidade = score?.gravidade ?? 'normal';
            const gStyle = GRAVIDADE_STYLE[gravidade];
            const isOpen = expanded === row.member.id;
            return (
              <div
                key={row.member.id}
                className={`border-b border-slate-100 last:border-b-0 dark:border-slate-800/60 ${gStyle.rowBg} ${gStyle.rowBorderLeft}`}
              >
                <div className="grid w-full grid-cols-[minmax(140px,1.1fr)_repeat(7,minmax(40px,1fr))] items-stretch">
                  <button
                    type="button"
                    onClick={() => toggle(row.member.id)}
                    className="flex items-center gap-1 border-r border-slate-100 py-1.5 pl-2 pr-2 text-left hover:bg-slate-50 dark:border-slate-800/60 dark:hover:bg-slate-800/40"
                  >
                    {isOpen ? (
                      <ChevronDown size={12} className="shrink-0 text-slate-400" />
                    ) : (
                      <ChevronRight size={12} className="shrink-0 text-slate-400" />
                    )}
                    <span className="min-w-0">
                      <span className="block truncate text-[11px] font-semibold text-slate-800 dark:text-slate-100">
                        {row.member.name}
                      </span>
                      <span className="block text-[9px] text-slate-400">{LST_CARGO_LABELS[row.cargo]}</span>
                    </span>
                  </button>

                  {row.dias.map((dia) => {
                    const dayGrav = resolveDayGravidade(dia.nivel);
                    const dayStyle = GRAVIDADE_STYLE[dayGrav];
                    const isActive =
                      activeDiagnostic?.memberId === row.member.id &&
                      activeDiagnostic.date === dia.date;
                    return (
                      <button
                        key={dia.date}
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          onOpenDiagnostic?.(row, dia.date);
                        }}
                        title={`${row.member.name} · ${ddmm(dia.date)}${
                          dia.desvios ? ` · ${dia.desvios} desvio(s)` : ''
                        }${dia.perdidas ? ` · ${dia.perdidas} perdida(s)` : ''}`}
                        className="flex items-center justify-center border-r border-slate-100 p-0.5 last:border-r-0 dark:border-slate-800/40"
                      >
                        <span
                          className={`flex h-7 w-full min-w-[36px] items-center justify-center rounded text-[9px] font-bold ${
                            dia.nivel === 'neutro'
                              ? 'bg-slate-100 text-slate-400 dark:bg-slate-800 dark:text-slate-500'
                              : `text-white ${NIVEL_CELL[dia.nivel]}`
                          } ${dayGrav !== 'normal' ? dayStyle.cellRing : ''} ${
                            dia.date === today ? 'ring-1 ring-blue-400' : ''
                          } ${isActive ? 'ring-2 ring-[#2F6FED]' : ''}`}
                        >
                          {dia.nivel === 'vermelho' && dia.perdidas > 0 ? dia.perdidas : ''}
                          {dia.nivel === 'laranja' && dia.desvios > 0 ? dia.desvios : ''}
                          {dia.nivel === 'verde' && dia.criticasTotal > 0 ? '✓' : ''}
                        </span>
                      </button>
                    );
                  })}
                </div>

                {isOpen && (
                  <div className="border-t border-slate-100 bg-slate-50/60 px-3 py-2 dark:border-slate-800/60 dark:bg-slate-900/30">
                    <Drilldown row={row} focusDate={focusDate} />
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

const Drilldown: React.FC<{ row: LstLideradoAderencia; focusDate: string | null }> = ({
  row,
  focusDate,
}) => {
  const ocorrencias = focusDate
    ? row.ocorrencias.filter((o) => o.date === focusDate)
    : row.ocorrencias;

  if (ocorrencias.length === 0) {
    return (
      <p className="text-[10px] text-emerald-600 dark:text-emerald-400">
        Sem desvios ou perdas {focusDate ? `em ${ddmm(focusDate)}` : 'na semana'}.
      </p>
    );
  }

  return (
    <ul className="space-y-1">
      {ocorrencias.map((o, idx) => {
        const isPerda = o.status === 'nao_realizado';
        const grav = isPerda ? 'critico' : 'atencao';
        const gStyle = GRAVIDADE_STYLE[grav];
        return (
          <li
            key={`${o.date}-${o.templateTitle}-${idx}`}
            className={`flex items-start gap-1.5 rounded border px-2 py-1 text-[10px] ${gStyle.border} ${isPerda ? 'bg-red-50/70 dark:bg-red-900/15' : 'bg-amber-50/70 dark:bg-amber-900/15'}`}
          >
            <span className={`mt-1 h-1.5 w-1.5 shrink-0 rounded-full ${isPerda ? 'bg-red-500' : 'bg-amber-500'}`} />
            <div className="min-w-0 flex-1">
              <span className="font-semibold text-slate-800 dark:text-slate-100">{o.templateTitle}</span>
              {o.isCritical && (
                <span className="ml-1 rounded bg-red-100 px-1 text-[8px] font-bold text-red-600">Crítica</span>
              )}
              <p className={`${isPerda ? 'text-red-600' : 'text-amber-700'} dark:opacity-90`}>
                {isPerda
                  ? 'Não realizado.'
                  : o.justificativa
                    ? o.justificativa
                    : 'Desvio sem justificativa.'}
              </p>
            </div>
            <span className="shrink-0 text-[9px] text-slate-400">{ddmm(o.date)}</span>
          </li>
        );
      })}
    </ul>
  );
};

const Legend: React.FC = () => (
  <div className="flex items-center gap-2 text-[9px] text-slate-500">
    <LegendDot cls="bg-emerald-500" label="Normal" />
    <LegendDot cls="bg-amber-400" label="Atenção" />
    <LegendDot cls="bg-red-500" label="Crítico" />
  </div>
);

const LegendDot: React.FC<{ cls: string; label: string }> = ({ cls, label }) => (
  <span className="inline-flex items-center gap-0.5">
    <span className={`h-2 w-2 rounded-sm ${cls}`} /> {label}
  </span>
);

const EmptyState: React.FC = () => (
  <TplEnterpriseEmptyState
    icon={Users2}
    title="Cadeia de Ajuda aguardando equipe"
    description="Nenhum liderado direto vinculado à sua hierarquia."
    hint="Administração → Pessoas → defina o gestor direto de cada colaborador."
  />
);

export default CadeiaAjudaPanel;
